import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { PLAYERS, BOARD_DIM } from "../lib/quattro";

// SVG overlay drawing an animated glowing beam through the winning cells.
// Plus a particle burst at the line's midpoint.
export default function WinHighlight({ winLine, winnerId }) {
  const particles = useMemo(() => {
    const arr = [];
    const count = 28;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
      const dist = 80 + Math.random() * 140;
      arr.push({
        id: i,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        size: 4 + Math.random() * 6,
        delay: Math.random() * 0.15,
        dur: 0.9 + Math.random() * 0.8,
      });
    }
    return arr;
  }, [winLine]);

  if (!winLine || winLine.length < 2) return null;
  const p = PLAYERS[winnerId];
  const start = winLine[0];
  const end = winLine[winLine.length - 1];
  // SVG viewBox is BOARD_DIM x BOARD_DIM; center of cell (r,c) = (c+0.5, r+0.5).
  const x1 = start[1] + 0.5;
  const y1 = start[0] + 0.5;
  const x2 = end[1] + 0.5;
  const y2 = end[0] + 0.5;
  // Particle burst at midpoint (in % of board)
  const midX = ((x1 + x2) / 2 / BOARD_DIM) * 100;
  const midY = ((y1 + y2) / 2 / BOARD_DIM) * 100;

  return (
    <div
      style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 5 }}
      data-testid="win-highlight"
    >
      <svg
        viewBox={`0 0 ${BOARD_DIM} ${BOARD_DIM}`}
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      >
        <defs>
          <filter id="winGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.08" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Soft halo */}
        <motion.line
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={p.color}
          strokeWidth="0.55"
          strokeLinecap="round"
          opacity={0.25}
          filter="url(#winGlow)"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
        {/* Bright beam */}
        <motion.line
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={p.color}
          strokeWidth="0.18"
          strokeLinecap="round"
          filter="url(#winGlow)"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1, opacity: [0, 1, 0.85] }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        />
        {/* Pulse circles at each winning cell */}
        {winLine.map(([r, c], i) => (
          <motion.circle
            key={i}
            cx={c + 0.5}
            cy={r + 0.5}
            r={0.42}
            fill="none"
            stroke={p.color}
            strokeWidth="0.06"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: [0, 1, 0], scale: [0.5, 1.4, 1.7] }}
            transition={{ duration: 1.0, delay: 0.4 + i * 0.08, repeat: 1 }}
            style={{ transformOrigin: `${c + 0.5}px ${r + 0.5}px` }}
          />
        ))}
      </svg>

      {/* Particle burst at midpoint */}
      <div
        style={{
          position: "absolute",
          left: `${midX}%`,
          top: `${midY}%`,
          width: 0,
          height: 0,
        }}
      >
        {particles.map((q) => (
          <motion.span
            key={q.id}
            style={{
              position: "absolute",
              left: 0, top: 0,
              width: q.size, height: q.size,
              marginLeft: -q.size / 2, marginTop: -q.size / 2,
              borderRadius: "50%",
              background: p.color,
              boxShadow: `0 0 ${q.size * 2}px ${p.glow}`,
            }}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
            animate={{ x: q.dx, y: q.dy, opacity: [0, 1, 0], scale: [0.4, 1, 0.5] }}
            transition={{ duration: q.dur, delay: 0.55 + q.delay, ease: "easeOut" }}
          />
        ))}
        {/* Central flash */}
        <motion.span
          style={{
            position: "absolute",
            left: 0, top: 0,
            width: 60, height: 60,
            marginLeft: -30, marginTop: -30,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${p.color}, transparent 70%)`,
            filter: "blur(6px)",
          }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: [0, 0.95, 0], scale: [0.6, 2.2, 2.6] }}
          transition={{ duration: 1.0, delay: 0.5 }}
        />
      </div>
    </div>
  );
}
